// ログイン画面（PASSWORD/PASSWORD_SRP, 設計 §8.2）。WCAG2: label 関連付け / aria-invalid / role=alert / フォーカス管理。
// 認可の正本はサーバ側。取得したアクセストークンは保護 API 呼び出しに使うのみ（ローカル認可判定に使わない, R1/R5）。
import type { AppConfig } from "../config";
import { createCognitoIdp, type AuthResult, type CognitoIdp } from "./cognito-idp";

export interface LoginDeps {
  readonly config: AppConfig;
  readonly onAuthenticated: (accessToken: string) => void;
  readonly onMfaSetupRequired: (session: string, username: string) => void;
  readonly idp?: CognitoIdp;
}

export function renderLogin(root: HTMLElement, deps: LoginDeps): void {
  const idp = deps.idp ?? createCognitoIdp(deps.config.region);
  root.replaceChildren();

  const form = document.createElement("form");
  form.noValidate = true;
  form.className = "space-y-4";
  form.setAttribute("aria-describedby", "login-error");

  const heading = document.createElement("h1");
  heading.className = "text-xl font-semibold text-slate-900";
  heading.textContent = "ログイン";

  const alert = document.createElement("div");
  alert.id = "login-error";
  alert.setAttribute("role", "alert");
  alert.setAttribute("aria-live", "assertive");
  alert.className = "min-h-[1.25rem] text-sm text-red-700";

  const email = field("email", "メールアドレス", "email", "username");
  const password = field("password", "パスワード", "password", "current-password");

  const mfaWrap = document.createElement("div");
  mfaWrap.className = "hidden space-y-1";
  const mfa = field("mfa-code", "認証アプリのコード（6桁）", "text", "one-time-code");
  mfa.input.inputMode = "numeric";
  mfa.input.autocomplete = "one-time-code";
  mfaWrap.append(mfa.label, mfa.input);

  const submitBtn = document.createElement("button");
  submitBtn.type = "submit";
  submitBtn.className =
    "w-full rounded bg-shinonome-700 px-4 py-2 font-medium text-white hover:bg-shinonome-800 disabled:opacity-60";
  submitBtn.textContent = "続行";

  form.append(heading, alert, email.label, email.input, password.label, password.input, mfaWrap, submitBtn);
  root.append(form);

  let session: string | null = null;
  let mfaPhase = false;

  function showError(message: string): void {
    alert.textContent = message;
    email.input.setAttribute("aria-invalid", "true");
    password.input.setAttribute("aria-invalid", "true");
  }

  function clearError(): void {
    alert.textContent = "";
    email.input.removeAttribute("aria-invalid");
    password.input.removeAttribute("aria-invalid");
  }

  function handleResult(result: AuthResult, username: string): void {
    const token = result.AuthenticationResult?.AccessToken;
    if (typeof token === "string" && token !== "") {
      deps.onAuthenticated(token);
      return;
    }
    if (result.ChallengeName === "MFA_SETUP") {
      deps.onMfaSetupRequired(result.Session ?? "", username);
      return;
    }
    if (result.ChallengeName === "SOFTWARE_TOKEN_MFA" || result.ChallengeName === "SMS_MFA") {
      session = result.Session ?? null;
      mfaPhase = true;
      mfaWrap.classList.remove("hidden");
      mfa.input.focus();
      return;
    }
    showError("対応していない認証チャレンジです。");
  }

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    clearError();
    submitBtn.disabled = true;

    const username = email.input.value.trim();

    const run = mfaPhase
      ? idp.respondToAuthChallenge({
          clientId: deps.config.spaClientId,
          challengeName: "SOFTWARE_TOKEN_MFA",
          session: session ?? "",
          challengeResponses: { USERNAME: username, SOFTWARE_TOKEN_MFA_CODE: mfa.input.value.trim() },
        })
      : idp.initiateAuth({
          clientId: deps.config.spaClientId,
          authFlow: "USER_PASSWORD_AUTH",
          authParameters: { USERNAME: username, PASSWORD: password.input.value },
        });

    run
      .then((r) => handleResult(r, username))
      .catch((err: unknown) => {
        showError(err instanceof Error ? readableMessage(err.message) : "サインインに失敗しました。");
      })
      .finally(() => {
        submitBtn.disabled = false;
      });
  });
}

function field(
  id: string,
  labelText: string,
  type: string,
  autocomplete: AutoFill,
): { label: HTMLLabelElement; input: HTMLInputElement } {
  const label = document.createElement("label");
  label.htmlFor = id;
  label.className = "block text-sm font-medium text-slate-700";
  label.textContent = labelText;

  const input = document.createElement("input");
  input.id = id;
  input.name = id;
  input.type = type;
  input.required = true;
  input.autocomplete = autocomplete;
  input.className =
    "mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-slate-900 focus:border-shinonome-700";
  return { label, input };
}

function readableMessage(raw: string): string {
  if (raw.includes("NotAuthorizedException")) return "メールアドレスまたはパスワードが正しくありません。";
  if (raw.includes("UserNotFoundException")) return "メールアドレスまたはパスワードが正しくありません。";
  if (raw.includes("CodeMismatchException")) return "認証コードが正しくありません。";
  return "サインインに失敗しました。時間をおいて再試行してください。";
}
